/**
 * Prefetcher — Background orchestrator that fetches report data for likely-needed
 * surveys after login, using idle time to avoid blocking the main thread.
 * 
 * Selects the most recently closed surveys with sufficient responses and prefetches
 * their report data into the DataCache. Results are stored using the same key scheme
 * as ControlPanel.fetchSurveyWithCache so that navigating to a report page gets an
 * instant cache hit.
 * 
 * @module prefetcher
 */

import dataCache from './dataCache.js';

/**
 * Schedule a callback during browser idle time.
 * Falls back to setTimeout(fn, 1) in environments without requestIdleCallback.
 * @param {Function} fn
 * @returns {number} Handle that can be used to cancel
 */
function scheduleIdle(fn) {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(fn);
  }
  return setTimeout(fn, 1);
}

/**
 * Cancel a previously scheduled idle callback.
 * @param {number} handle
 */
function cancelIdle(handle) {
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(handle);
  } else {
    clearTimeout(handle);
  }
}

export class Prefetcher {
  /**
   * @param {import('./dataCache.js').DataCache} dataCacheInstance - Cache instance to store prefetched data
   * @param {Object} databaseService - DatabaseService instance with .collection() method
   */
  constructor(dataCacheInstance, databaseService) {
    this._dataCache = dataCacheInstance || dataCache;
    this._db = databaseService;
    this._active = false;
    this._aborted = false;
    this._inProgressSurveys = new Set();
    this._timeoutIds = [];
    this._idleHandle = null;
  }

  /**
   * Start prefetching report data for recent surveys.
   * Selects up to `maxSurveys` closed surveys sorted by closeDate descending,
   * filtered to those with sufficient responses, and fetches their report data
   * one at a time with 100ms gaps using idle callbacks.
   * 
   * @param {Array} surveys - Array of survey objects with closeDate, _id, responseCount/responses
   * @param {Object} items - Items lookup map (id -> key), used to determine comment exclusions
   * @param {number} [maxSurveys=3] - Maximum number of surveys to prefetch
   */
  start(surveys, items, maxSurveys = 3) {
    if (this._active) return;

    this._active = true;
    this._aborted = false;

    const selected = this._selectSurveys(surveys, maxSurveys);
    if (selected.length === 0) {
      this._active = false;
      return;
    }

    // Schedule the prefetch chain during idle time
    this._idleHandle = scheduleIdle(() => {
      if (this._aborted) return;
      this._prefetchChain(selected, items, 0);
    });
  }

  /**
   * Stop any in-progress prefetching (e.g., on logout).
   * Clears pending timeouts and marks the session as aborted.
   */
  stop() {
    this._aborted = true;
    this._active = false;

    // Cancel any pending idle callback
    if (this._idleHandle !== null) {
      cancelIdle(this._idleHandle);
      this._idleHandle = null;
    }

    // Clear all pending timeouts
    for (const id of this._timeoutIds) {
      clearTimeout(id);
    }
    this._timeoutIds = [];
    this._inProgressSurveys.clear();
  }

  /**
   * Check if a specific survey's data is currently being prefetched.
   * @param {string} surveyId - The survey's $oid string
   * @returns {boolean}
   */
  isInProgress(surveyId) {
    return this._inProgressSurveys.has(surveyId);
  }

  /**
   * Select surveys eligible for prefetching.
   * Filters to closed surveys with sufficient responses (responseCount > 0),
   * sorts by closeDate descending (most recent first), and returns up to maxSurveys.
   * 
   * @param {Array} surveys - All available surveys
   * @param {number} maxSurveys - Maximum to select
   * @returns {Array} Selected surveys for prefetching
   */
  _selectSurveys(surveys, maxSurveys) {
    const now = new Date();

    const closed = surveys.filter(survey => {
      // Must have a closeDate that is in the past
      if (!survey.closeDate) return false;
      const closeDate = new Date(survey.closeDate);
      if (isNaN(closeDate.getTime()) || closeDate > now) return false;

      // Must have sufficient responses (> 0)
      const responseCount = survey.responseCount ?? survey.responses ?? 0;
      return responseCount > 0;
    });

    // Sort by closeDate descending (most recent first)
    closed.sort((a, b) => new Date(b.closeDate) - new Date(a.closeDate));

    return closed.slice(0, maxSurveys);
  }

  /**
   * Sequentially prefetch surveys one at a time with 100ms gaps.
   * @param {Array} surveys - Selected surveys to prefetch
   * @param {Object} items - Items lookup map
   * @param {number} index - Current index in the survey list
   */
  _prefetchChain(surveys, items, index) {
    if (this._aborted || index >= surveys.length) {
      this._active = false;
      return;
    }

    const survey = surveys[index];
    const surveyId = survey._id.$oid || survey._id;

    this._inProgressSurveys.add(surveyId);

    this._prefetchSurvey(survey, items)
      .catch(err => {
        // Silent failure — prefetch is best-effort
        console.warn(`[Prefetcher] Failed to prefetch survey ${surveyId}:`, err.message);
      })
      .finally(() => {
        this._inProgressSurveys.delete(surveyId);

        if (this._aborted) {
          this._active = false;
          return;
        }

        // Schedule next survey fetch with 100ms gap
        const timeoutId = setTimeout(() => {
          this._timeoutIds = this._timeoutIds.filter(id => id !== timeoutId);
          this._prefetchChain(surveys, items, index + 1);
        }, 100);

        this._timeoutIds.push(timeoutId);
      });
  }

  /**
   * Prefetch report data for a single survey using the DataCache.
   * Uses the same key scheme as ControlPanel.fetchSurveyWithCache:
   * collection='Responses', operation='report-data', params={ surveyId }
   * 
   * This means if the data is already cached, no fetch will occur.
   * If the ControlPanel later requests the same data, it will get a cache hit.
   * 
   * KEY SCHEME ALIGNMENT (Requirements 4.3, 4.4):
   * - Prefetcher uses: dataCache.get('Responses', 'report-data', { surveyId }, ...)
   * - ControlPanel uses the same key for non-comment pages
   * - DataCache.get() deduplicates via the in-flight registry:
   *   - If prefetch is IN PROGRESS when user navigates → attaches to existing promise (no duplicate request)
   *   - If prefetch is COMPLETE when user navigates → serves from cache immediately
   * - Comment pages use { surveyId, full: true } — a different key, so they fetch separately (correct behaviour)
   * 
   * @param {Object} survey - Survey object with _id.$oid
   * @param {Object} items - Items lookup map (id -> key)
   * @returns {Promise<any>}
   */
  async _prefetchSurvey(survey, items) {
    const surveyId = survey._id.$oid || survey._id;

    // Comment item keys that contain verbose text — excluded from non-comment reports.
    // Must match the COMMENT_KEYS set in ControlPanel.js.
    const COMMENT_KEYS = new Set([
      'comments', 'b21', 'b22', 'b23', 'rn50', 'rn51', 'rn52', 'rn53',
      'rn77', 'rn78', 'rn79', 'rn110', 'rn200', 'rn201', 'rn202', 'rn203', 'rn204',
      'ipe43', 'ipe44', 'ipe45', 'jg26', 'jg27', 'jg134', 'jg135', 'jg136',
      'k13', 'k14', 'k15', 'k16', 'k17'
    ]);

    // Determine comment item IDs to exclude (matching ControlPanel behaviour)
    const commentItemIds = items
      ? Object.entries(items)
          .filter(([id, key]) => COMMENT_KEYS.has(key))
          .map(([id]) => id)
      : [];

    // Use dataCache.get with the same key scheme as ControlPanel
    // If data is already cached, this returns immediately without a fetch
    return this._dataCache.get(
      'Responses',
      'report-data',
      { surveyId },
      async () => {
        return this._db.collection('Responses').reportData(surveyId, commentItemIds);
      }
    );
  }
}

export default Prefetcher;
